import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SeoHead from '@/components/feature/SeoHead';
import JsonLd from '@/components/feature/JsonLd';
import PageHero from '@/components/feature/PageHero';
import ActivityCard from '@/components/feature/ActivityCard';
import Reveal from '@/components/feature/Reveal';
import { activities, activityCategories } from '@/mocks/activities';

export default function Activities() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get('cat') || 'all';
  const [keyword, setKeyword] = useState(searchParams.get('q') || '');

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return activities.filter((item) => {
      const matchCat = activeCat === 'all' || item.category === activeCat;
      const haystack = [item.title, item.summary, item.reward, ...item.tags].join(' ').toLowerCase();
      const matchKw = kw === '' || haystack.includes(kw);
      return matchCat && matchKw;
    });
  }, [activeCat, keyword]);

  const setCategory = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'all') next.delete('cat');
    else next.set('cat', id);
    setSearchParams(next, { replace: true });
  };

  const resetAll = () => {
    setKeyword('');
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: '福利活动中心 - 爆瓜福利',
        description: '集中浏览爆瓜福利正在进行的签到、邀请、互动、抽奖等活动，支持按玩法分类筛选与关键词搜索。',
        url: 'https://ikinxod.cn/activities',
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://ikinxod.cn/' },
          { '@type': 'ListItem', position: 2, name: '福利活动', item: 'https://ikinxod.cn/activities' },
        ],
      },
      {
        '@type': 'ItemList',
        name: '福利活动列表',
        numberOfItems: activities.length,
        itemListElement: activities.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.title,
          url: `https://ikinxod.cn/activities/${item.id}`,
        })),
      },
    ],
  };

  return (
    <>
      <SeoHead
        path="/activities"
        title="福利活动中心 - 爆瓜福利活动大全与分类筛选"
        description="爆瓜福利活动中心集中展示每日签到、邀请好友、内容互动、限时抽奖等福利活动，支持按玩法分类筛选与关键词搜索，快速找到适合自己的任务与奖励。"
        keywords="爆瓜福利活动,福利活动中心,签到活动,邀请好友,限时抽奖,任务奖励"
      />
      <JsonLd data={structuredData} />

      <Layout>
        <PageHero
          icon="ri-gift-line"
          eyebrow="福利活动中心"
          title="所有能参与的活动，都集中在这里"
          desc="按玩法分类筛选，或直接搜索关键词。每个活动都标注了奖励内容、参与难度与截止时间，挑之前先对比一下更省事。"
          crumbs={[{ label: '首页', path: '/' }, { label: '福利活动' }]}
        >
          <div className="flex flex-wrap gap-3">
            <div className="px-4 py-3 rounded-xl bg-background-50 border border-background-200">
              <p className="text-[11px] text-foreground-500">在架活动</p>
              <p className="text-lg text-foreground-950 font-heading">{activities.length} 个</p>
            </div>
            <div className="px-4 py-3 rounded-xl bg-background-50 border border-background-200">
              <p className="text-[11px] text-foreground-500">热门推荐</p>
              <p className="text-lg text-primary-600 font-heading">
                {activities.filter((a) => a.hot).length} 个
              </p>
            </div>
          </div>
        </PageHero>

        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-50">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
            <div className="flex flex-wrap items-center gap-2">
              {activityCategories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    activeCat === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 w-full lg:w-auto">
              <div className="relative flex-1 lg:w-72">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
                  <i className="ri-search-line"></i>
                </span>
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索活动名称或奖励"
                  aria-label="搜索福利活动"
                  className="w-full pl-11 pr-4 py-2.5 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between gap-4">
            <p className="text-sm text-foreground-600">
              共找到 <span className="text-primary-700 font-medium">{filtered.length}</span> 个活动
            </p>
            {(activeCat !== 'all' || keyword.trim() !== '') && (
              <button
                type="button"
                onClick={resetAll}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                清除筛选
              </button>
            )}
          </div>

          {filtered.length > 0 ? (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filtered.map((activity, index) => (
                <Reveal key={activity.id} delay={(index % 6) * 60}>
                  <ActivityCard activity={activity} />
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="mt-8 bg-background-100 border border-background-200 rounded-2xl py-16 px-6 text-center">
              <span className="inline-flex w-14 h-14 rounded-full bg-background-200 text-foreground-600 items-center justify-center text-2xl">
                <i className="ri-emotion-unhappy-line"></i>
              </span>
              <h2 className="mt-4 text-lg text-foreground-950">没有匹配的活动</h2>
              <p className="mt-2 text-sm text-foreground-600">换个关键词，或者先看看全部活动吧。</p>
              <button
                type="button"
                onClick={resetAll}
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-go-back-line"></i>
                查看全部活动
              </button>
            </div>
          )}
        </section>
      </Layout>
    </>
  );
}